// Copyright (c) 2025, WSO2 LLC. (https://www.wso2.com). All Rights Reserved.
//
// This software is the property of WSO2 LLC. and its suppliers, if any.
// Dissemination of any information or reproduction of any material contained
// herein in any form is strictly forbidden, unless permitted by WSO2 expressly.
// You may not alter or remove any copyright or other notice from copies of this content.

import React, { useCallback, useEffect, useState } from "react";
import AddCircleOutlineIcon from "@mui/icons-material/AddCircleOutline";
import Button from "@mui/material/Button";
import ConfirmationDialog from "../dialog/ConfirmationDialog.js";
import CustomSnackbar from "../ui/Snackbars.js";
import Grid from "@mui/material/Grid";
import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableCell from "@mui/material/TableCell";
import TableContainer from "@mui/material/TableContainer";
import TableHead from "@material-ui/core/TableHead";
import TableRow from "@mui/material/TableRow";
import TextField from "@mui/material/TextField";
import useHttp from "../../utils/http.js";
import { travelCardCurrencyTypes, travelStatus } from "../../utils/const.js";
import {
  getTravelCards,
  postTravelCards,
  getTotalPerDiemPaymentsCount,
} from "../../utils/end-points.js";
import { isAccessGranted } from "../../utils/oauth.js";
import { MenuItem, Select } from "@mui/material";
import { PRIVILEGES } from "../../Config.js";
import "../../css/TravelCard.scss";

const headCells = [
  {
    id: "travel_card_currency",
    label: "Travel Card Currency",
    align: "left",
    disablePadding: false,
  },
  {
    id: "travel_card_amount",
    label: "Travel Card Amount",
    align: "left",
    disablePadding: false,
  },
];

function EnhancedTableHead(props) {
  const { order, orderBy } = props;

  return (
    <TableHead>
      <TableRow>
        {headCells.map((headCell) => (
          <TableCell
            key={headCell.id}
            align={headCell.align}
            padding={headCell.disablePadding ? "none" : "normal"}
            sortDirection={orderBy === headCell.id ? order : false}
            className="Head-label"
          >
            {headCell.label}
          </TableCell>
        ))}
      </TableRow>
    </TableHead>
  );
}

const TravelCard = (props) => {
  const { travel, fetchPerdiemPaymentSummary,isLoading, setIsLoading } = props;
  const { handleRequest } = useHttp();
  const [travelCards, setTravelCards] = useState([]);
  const [isSaveEnable, setSaveEnable] = useState(false);
  const [isResetEnable, setResetEnable] = useState(false);
  const [editingRowId, setEditingRowId] = useState(null);
  const [confirmationDialogData, setConfirmationDialogData] = useState({
    open: false,
    title: "",
    content: "",
    acceptLabel: "",
    declineLabel: "",
    acceptAlert: null,
    declineAlert: null,
  });
  const [paymentCount, setPaymentCount] = useState(0);
  const [snackbarData, setSnackbarData] = useState({
    open: false,
    message: "",
    severity: "",
    onClose: null,
  });

  useEffect(() => {
    fetchTravelCardDetails(false);
  }, []);

  const fetchTravelCardDetails = useCallback(async (showSuccessSnack) => {
    setIsLoading(true);
    setEditingRowId(null);
    setResetEnable(false);
    setSaveEnable(false);
    const promises = [];

    // Fetch travel card details
    promises.push(
      new Promise((resolve, reject) => {
        handleRequest(
          getTravelCards.replace("$jobNumber", travel.jobNumber),
          "GET",
          null,
          (travelCards) => {
            if (travelCards) {
              setTravelCards(travelCards);
              resolve();
            } else {
              setSnackbarData({
                message: "Error while fetching travel card details",
                open: true,
                severity: "error",
                onClose: onCloseSnackbar,
              });
              reject();
            }
          },
          (error) => {
            setSnackbarData({
              message: error,
              open: true,
              severity: "error",
              onClose: onCloseSnackbar,
            });
            reject();
          }
        );
      })
    );
    if (isAccessGranted([PRIVILEGES.ADD_TRAVEL_CARDS])) {
      // Fetch total per diem payments count
      promises.push(
        new Promise((resolve, reject) => {
          handleRequest(
            getTotalPerDiemPaymentsCount.replace(
              "$jobNumber",
              travel.jobNumber
            ),
            "GET",
            null,
            (paymentCount) => {
              if (paymentCount !== undefined) {
                setPaymentCount(paymentCount);
                resolve();
              } else {
                setSnackbarData({
                  message: "Error while fetching total per diem payments count",
                  open: true,
                  severity: "error",
                  onClose: onCloseSnackbar,
                });
                reject();
              }
            },
            (error) => {
              setSnackbarData({
                message: error,
                open: true,
                severity: "error",
                onClose: onCloseSnackbar,
              });
              reject();
            }
          );
        })
      );
    }

    // Execute all promises and handle success/failure
    Promise.all(promises)
      .then(() => {
        if (showSuccessSnack) {
          setSnackbarData({
            message: "Success",
            open: true,
            severity: "success",
            onClose: onCloseSnackbar,
          });
        }
      })
      .catch((error) => {
        setSnackbarData({
          message: error,
          open: true,
          severity: "error",
          onClose: onCloseSnackbar,
        });
      })
      .finally(() => {
        setIsLoading(false);
      });
  }, []);

  const validateAndAddTravelCards = () => {
    setSaveEnable(false);

    // Check if the payment amount is greater than 0
    if (paymentCount > 0) {
      setSnackbarData({
        message:
          "The finance team has started processing the payments. Adding travel cards is not permitted at this time.",
        open: true,
        severity: "error",
        onClose: onCloseSnackbar,
      });
      return;
    }

    const newTravelCards = travelCards.filter(
      (travelCard) => travelCard.isNewTravelCard === true
    );
    if (newTravelCards.length > 0) {
      let lastAddedTravelCard = newTravelCards[newTravelCards.length - 1];
      lastAddedTravelCard = validateTravelCardInformation(
        lastAddedTravelCard,
        true
      );
      if (!lastAddedTravelCard.isError) {
        setTravelCards([...travelCards, addNewTravelCard()]);
      } else {
        setSaveEnable(false);
      }
    } else {
      let travelCardObj = addNewTravelCard();
      setTravelCards([...travelCards, travelCardObj]);
      if (!validateTravelCardInformation(travelCardObj).isError) {
        setSaveEnable(true);
      }
    }
  };

  const addNewTravelCard = () => {
    setEditingRowId(travelCards.length);
    const travelCardObj = {
      isNewTravelCard: true,
      isError: false,
    };
    setResetEnable(true);
    return travelCardObj;
  };

  const validateTravelCardInformation = (travelCardObj, showError) => {
    travelCardObj.isError = false;
    let errorMessage = "Please re-check the information. ";

    if (!travelCardObj.travelCardCurrency || !travelCardObj.travelCardAmount) {
      let errorCount = 0;
      travelCardObj.isError = true;

      if (showError) {
        if (!travelCardObj.travelCardCurrency) {
          errorMessage = errorMessage.concat("Travel Card Currency, ");
          errorCount += 1;
        }
        if (!travelCardObj.travelCardAmount) {
          errorMessage = errorMessage.concat("Travel Card Amount, ");
          errorCount += 1;
        }
        errorMessage = errorMessage.slice(0, -2);
        if (errorCount > 1) {
          errorMessage = errorMessage.concat(" are mandatory.");
        } else {
          errorMessage = errorMessage.concat(" is mandatory.");
        }
        setSnackbarData({
          message: errorMessage,
          open: true,
          severity: "error",
          onClose: onCloseSnackbar,
        });
      } else {
        setSnackbarData({
          message: "",
          open: false,
          severity: "error",
          onClose: onCloseSnackbar,
        });
      }
    }
    return travelCardObj;
  };

  const getEditField = (id, fieldName) => {
    switch (fieldName) {
      case "travelCardCurrency":
        return (
          <Select
            value={travelCards[id][fieldName]}
            fullWidth
            onChange={(event) =>
              setEditFieldValue(event.target.value, id, fieldName)
            }
            size="small"
          >
            {travelCardCurrencyTypes.length > 0 &&
              travelCardCurrencyTypes.map((currency) => {
                return (
                  <MenuItem key={currency[0]} value={currency[0]}>
                    <span>{currency[0] + " (" + currency[1] + ")"}</span>
                  </MenuItem>
                );
              })}
          </Select>
        );

      case "travelCardAmount":
        return (
          <TextField
            variant="outlined"
            size="small"
            type="number"
            fullWidth
            value={travelCards[id][fieldName] ? travelCards[id][fieldName] : ""}
            onChange={(event) =>
              setEditFieldValue(parseFloat(event.target.value), id, fieldName)
            }
          />
        );

      default:
        return (
          <TextField
            variant="outlined"
            size="small"
            value={travelCards[id][fieldName] ? travelCards[id][fieldName] : ""}
            onChange={(event) =>
              setEditFieldValue(event.target.value, id, fieldName)
            }
          />
        );
    }
  };

  const setEditFieldValue = (value, id, fieldName) => {
    let travelCardObj = travelCards[id];
    travelCardObj[fieldName] = value;

    travelCardObj = validateTravelCardInformation(travelCardObj, false);
    if (travelCardObj.isError) {
      setSaveEnable(false);
    } else {
      let travelCardArr = [...travelCards];
      travelCardArr[id] = travelCardObj;
      setTravelCards(travelCardArr);
      setSaveEnable(true);
    }
  };

  const resetTravelCards = () => {
    setConfirmationDialogData({
      open: true,
      title: "Reset Travel Cards",
      content:
        "Are you sure you want reset travel cards into last saved values?",
      acceptLabel: "Yes",
      declineLabel: "No",
      acceptAlert: handleAcceptResetAlert,
      declineAlert: handleDeclineAlert,
    });
  };

  const saveTravelCards = () => {
    setConfirmationDialogData({
      open: true,
      title: "Save Travel Cards",
      content: "Are you sure you want to save these travel cards?",
      acceptLabel: "Yes",
      declineLabel: "No",
      acceptAlert: handleAcceptSaveAlert,
      declineAlert: handleDeclineAlert,
    });
  };

  const handleAcceptResetAlert = () => {
    setConfirmationDialogData((data) => ({ ...data, open: false }));
    fetchTravelCardDetails(false);
  };

  const handleAcceptSaveAlert = useCallback(async () => {
    setConfirmationDialogData((data) => ({ ...data, open: false }));
    setIsLoading(true);
    const newTravelCards = travelCards
      .filter((travelCard) => travelCard.isNewTravelCard === true)
      .map(({ travelCardAmount, travelCardCurrency }) => ({
        travelCardAmount,
        travelCardCurrency,
      }));

    handleRequest(
      postTravelCards.replace("$jobNumber", travel.jobNumber),
      "POST",
      newTravelCards,
      (response) => {
        if (response) {
          fetchTravelCardDetails(true);
          fetchPerdiemPaymentSummary();
        } else {
          setIsLoading(false);
          setSnackbarData({
            message: "Error while inserting travel cards",
            open: true,
            severity: "error",
            onClose: onCloseSnackbar,
          });
        }
      },
      (error) => {
        setIsLoading(false);
        setSnackbarData({
          message: error,
          open: true,
          severity: "error",
          onClose: onCloseSnackbar,
        });
      }
    );
  });

  const handleDeclineAlert = () => {
    setConfirmationDialogData((data) => ({ ...data, open: false }));
  };

  const onCloseSnackbar = () => {
    setSnackbarData({ ...snackbarData, open: false });
  };

  return (
    <div className="Travel-card-information">
      <ConfirmationDialog confirmationDialogData={confirmationDialogData} />
      <CustomSnackbar snackbar={snackbarData} />

      <div>
        <TableContainer className="Table-container">
          <Table size={"small"}>
            <EnhancedTableHead />
            <TableBody>
              {travelCards.map((row, key) => {
                return (
                  <TableRow hover tabIndex={-1} key={key}>
                    <TableCell className="Table-cell Table-cell-currency">
                      {key === editingRowId
                        ? getEditField(key, "travelCardCurrency")
                        : row.travelCardCurrency}
                    </TableCell>
                    <TableCell className="Table-cell Table-cell-cost">
                      {key === editingRowId
                        ? getEditField(key, "travelCardAmount")
                        : row.travelCardAmount}
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
          {!isLoading && travelCards.length === 0 && (
            <div className="No-travel-cards-label">No Travel Cards</div>
          )}
        </TableContainer>
        <Grid container className="Button-grid">
          <Grid item lg={3} className="Button-div-add-travel-cards">
            {isAccessGranted([PRIVILEGES.ADD_TRAVEL_CARDS]) && (
              <Button
                variant="contained"
                size="small"
                color="secondary"
                className="Add-travel-card-button"
                disabled={
                  !(
                    travel.status === travelStatus.INPROGRESS ||
                    travel.status === travelStatus.EXTENDED
                  )
                }
                startIcon={<AddCircleOutlineIcon />}
                onClick={validateAndAddTravelCards}
              >
                Add Travel Card
              </Button>
            )}
          </Grid>
          {isAccessGranted([PRIVILEGES.ADD_TRAVEL_CARDS]) && (
            <Grid item lg={9} className="Button-div-save-reset">
              <Button
                variant="contained"
                size="small"
                color="secondary"
                className="Save-button"
                disabled={!isSaveEnable || travelCards.length === 0}
                onClick={saveTravelCards}
              >
                Save
              </Button>
              <Button
                variant="outlined"
                size="small"
                color="primary"
                className="Reset-button"
                disabled={!isResetEnable}
                onClick={resetTravelCards}
              >
                Reset
              </Button>
            </Grid>
          )}
        </Grid>
      </div>
    </div>
  );
};

export default TravelCard;
